import { cn } from "../../lib/utils.js";

/**
 * Control segmentado: N opciones, una activa. Filtros de lista y checklists.
 *
 * Es UN SOLO objeto partido en tramos, no una bandeja con pastillas adentro.
 * El contenedor no lleva borde ni relleno interior: los tramos llegan al canto
 * y el `overflow-hidden` les recorta las esquinas de los extremos. Todo el
 * conjunto es el control —`shadow-control`, como un botón— y el color del
 * tramo activo dice cuál está elegido.
 *
 * Antes esto era un carril hundido con una pastilla en relieve adentro, y se
 * leía como dos cajas anidadas: el borde del carril más el canto de la
 * pastilla dibujaban una caja dentro de otra en cada una de las 19 filas del
 * checklist.
 *
 * `bg-surface-2` y no `surface-1` es lo que sostiene el control ahora que no
 * hay borde: su contenedor habitual (la sección del checklist, un SlidePanel)
 * ES `surface-1`, así que con el mismo relleno y sin borde no quedaría nada
 * separándolo del fondo — el mismo agujero por el que el buscador de este
 * módulo desaparecía en tema claro.
 *
 * Accesibilidad: `role="group"` con botones `aria-pressed`. No es un
 * `radiogroup` a propósito — sin tabindex móvil un radiogroup real deja los
 * segmentos inalcanzables con Tab, y los 19 ítems del checklist se recorren
 * con Tab.
 *
 * @param {string} value — valor activo.
 * @param {(v: string) => void} onChange
 * @param {string} label — nombre accesible del grupo.
 * @param {Array<{
 *   value: string,
 *   label: string,
 *   count?: number,
 *   ariaLabel?: string,
 *   activeClassName?: string,
 * }>} options
 *   `activeClassName` pinta el segmento activo con su propio color (los
 *   estados bueno/regular/malo del checklist). Sin él usa el relieve neutro.
 * @param {"sm"|"md"} [size="md"] — `md` cumple los 44px de área táctil.
 */
export function ToggleGroup({
  value,
  onChange,
  label,
  options,
  size = "md",
  className,
  ...props
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className={cn(
        // UN SOLO objeto dividido en tramos, no una bandeja con pastillas
        // adentro. Sin borde propio y sin relleno interior, los tramos llegan
        // al canto y el `overflow-hidden` les recorta las esquinas: el
        // resultado se lee como un botón partido, que es lo que es.
        //
        // La superficie es `surface-2` y no `surface-1` justamente porque acá
        // se fue el borde: el contenedor habitual de este control (la sección
        // del checklist, un SlidePanel) es `surface-1`, así que sin borde Y
        // con el mismo relleno no quedaría NADA que lo separe del fondo. Un
        // peldaño arriba lo sostiene solo, y `shadow-control` lo despega.
        "inline-flex shrink-0 overflow-hidden rounded-lg",
        "bg-surface-2 sheen-control shadow-control",
        className,
      )}
      {...props}
    >
      {options.map((opt, i) => {
        const active = value === opt.value;
        // La divisoria se calla cuando toca un tramo activo: el relleno de
        // color ya es su propio límite, y una línea encima le comería el canto
        // al bloque —justo el efecto de "caja dentro de caja" que este diseño
        // viene a sacar—.
        const separador =
          i > 0 && !active && value !== options[i - 1]?.value;
        return (
          <button
            key={opt.value}
            type="button"
            aria-pressed={active}
            aria-label={opt.ariaLabel}
            onClick={() => onChange?.(opt.value)}
            className={cn(
              // Sin `rounded-*`: un tramo no es una pastilla suelta. Las
              // esquinas de los extremos se las da el `overflow-hidden` del
              // contenedor, así que el primero y el último redondean solos y
              // los del medio quedan rectos.
              "flex flex-1 items-center justify-center gap-1.5",
              "text-sm font-semibold whitespace-nowrap",
              "transition-[color,background-color] duration-150",
              // El foco va HACIA ADENTRO (`ring-inset`): sin borde ni relleno
              // que lo contengan, un anillo normal se dibuja por fuera del
              // canto y el `overflow-hidden` se lo come por tres lados.
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/70",
              // El área táctil son los 44px del TRAMO. `sm` es para barras de
              // filtro de escritorio, donde el objetivo es el ratón.
              size === "sm" ? "min-h-8 px-2.5" : "min-h-11 px-3",
              separador && "border-l border-border-muted",
              active
                ? // El relleno llega de canto a canto y NO lleva sheen: sobre
                  // un color pleno ese degradado es 16% de blanco encima del
                  // tono, y en un bloque de 44px no se lee como luz sino como
                  // neblina — el color se ensucia y el texto pierde filo.
                  opt.activeClassName || "bg-surface-3 text-text-primary"
                : "text-text-muted hover:bg-surface-3/60 hover:text-text-primary",
            )}
          >
            {opt.label}
            {opt.count != null && (
              <span
                className={cn(
                  "rounded-full px-1.5 text-xs font-medium tabular-nums",
                  // Tinte del color de TEXTO del tema, no un negro fijo: sobre
                  // un tramo activo oscuro un velo negro no se ve. Y el
                  // apagado va un peldaño por encima del contenedor
                  // (`surface-3` sobre `surface-2`), que si no desaparece.
                  // `bg-current/15` sería lo ideal pero Tailwind 3 no aplica el
                  // modificador de opacidad a `currentColor`.
                  active ? "bg-text-primary/15 text-inherit" : "bg-surface-3 text-text-dim",
                )}
              >
                {opt.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
ToggleGroup.displayName = "ToggleGroup";
